import axios from "axios";
import { LOG_OUT, TOASTER_SHOW } from "../constant";
import { get as getLocalStorage } from "../services/localStorage";
import { clearSyncedDealersSession } from "./dealerSessionSync";

let loggingOut = false;

export function getStoredAuthToken() {
  try {
    return getLocalStorage({ key: "auth" })?.token || "";
  } catch {
    return "";
  }
}

function readBodyText(data) {
  if (data == null) return "";
  if (typeof data === "string") return data;
  if (typeof data?.message === "string") {
    return [
      data.message,
      data.detail,
      data.error,
      data.description,
    ]
      .filter(Boolean)
      .join(" ");
  }
  return [
    data.detail,
    data.error,
    data.description,
    data.message?.description,
    data.message?.heading,
    data.message?.detail,
    data.message?.message,
    data.message?.type,
    data.data?.detail,
    data.data?.message,
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Only treat explicit "token not authorized / invalid token" API messages
 * as a forced logout. Do NOT logout on bare HTTP 401.
 */
export function isTokenUnauthorizedResponse(_status, data) {
  const detail = readBodyText(data).toLowerCase().replace(/\s+/g, " ").trim();
  if (!detail) return false;

  return (
    /token\s+(is\s+)?not\s+authori[sz]ed/.test(detail) ||
    /not\s+authori[sz]ed\s+(by\s+)?token/.test(detail) ||
    /unauthori[sz]ed\s+token/.test(detail) ||
    /token\s+is\s+unauthori[sz]ed/.test(detail) ||
    /invalid\s+token/.test(detail) ||
    /token\s+(has\s+)?expired/.test(detail) ||
    /authentication\s+credentials\s+were\s+not\s+provided/.test(detail) ||
    detail === "not authenticated" ||
    detail === "authentication credentials were not provided."
  );
}

/** @deprecated use isTokenUnauthorizedResponse */
export function isAuthExpiredResponse(status, data) {
  return isTokenUnauthorizedResponse(status, data);
}

function isAuthExemptUrl(url = "") {
  return /\/(login|signup|sign[_-]?up|register|forget|password_reset|resetpassword|check_reg|car_reg)\b/i.test(
    String(url)
  );
}

/**
 * Clear client (+ dealers) session and send the user to login.
 */
export function forceSessionLogout({
  reason = "Your session is not authorized. Please log in again.",
  redirect = true,
} = {}) {
  if (loggingOut) return;
  if (!getStoredAuthToken()) return;
  loggingOut = true;

  try {
    // eslint-disable-next-line global-require
    const storeModule = require("../store").default;
    const dispatch = storeModule?.store?.dispatch;
    if (typeof dispatch === "function") {
      dispatch({ type: LOG_OUT });
      dispatch({
        type: TOASTER_SHOW,
        payload: {
          show: true,
          autoHide: true,
          type: "error",
          heading: "Not authorized",
          description: reason,
        },
      });
    }
  } catch {
    try {
      window.localStorage.removeItem("ATP_localstorage_auth");
    } catch {
      /* ignore */
    }
  }

  try {
    clearSyncedDealersSession();
  } catch {
    /* ignore */
  }

  if (!redirect || typeof window === "undefined") {
    loggingOut = false;
    return;
  }

  const path = window.location?.pathname || "";
  if (/^\/(login|signup|forgetpassword)(\/|$)/i.test(path)) {
    loggingOut = false;
    return;
  }

  window.setTimeout(() => {
    window.location.assign("/login?expired=1");
  }, 50);
}

/**
 * Inspect an API payload and logout only when the token is explicitly
 * reported as not authorized / invalid.
 */
export function handlePossiblyExpiredAuth({ status, data, url } = {}) {
  if (isAuthExemptUrl(url)) return false;
  if (!getStoredAuthToken()) return false;
  if (!isTokenUnauthorizedResponse(status, data)) return false;
  forceSessionLogout({
    reason:
      readBodyText(data) ||
      "Your token is not authorized. Please log in again.",
  });
  return true;
}

/** Attach interceptors that logout only on unauthorized-token API messages. */
export function installAuthExpiryInterceptors(axiosInstance) {
  const client = axiosInstance || axios;
  if (client.__atpAuthExpiryInstalled) return client;
  client.__atpAuthExpiryInstalled = true;

  client.interceptors.response.use(
    (response) => {
      handlePossiblyExpiredAuth({
        status: response?.status,
        data: response?.data,
        url: response?.config?.url || "",
      });
      return response;
    },
    (error) => {
      handlePossiblyExpiredAuth({
        status: error?.response?.status,
        data: error?.response?.data,
        url: error?.config?.url || "",
      });
      return Promise.reject(error);
    }
  );

  return client;
}

/** Call once at app boot — covers default axios + shared Api client. */
export function bootstrapAuthExpiryHandling() {
  installAuthExpiryInterceptors(axios);
  try {
    // eslint-disable-next-line global-require
    const { Api } = require("../services/api");
    if (Api) installAuthExpiryInterceptors(Api);
  } catch {
    /* ignore */
  }
  try {
    // eslint-disable-next-line global-require
    const dealersApi = require("../dealers/services/api");
    if (dealersApi?.Api) installAuthExpiryInterceptors(dealersApi.Api);
  } catch {
    /* ignore */
  }
}
